﻿<script setup lang="ts">
    import { reactive, inject, onMounted, computed } from 'vue';
    import List from '@shared/components/List.vue';
    import { type tabs } from '@shared/components/ListTypes';
    import ListItem from './ListItem.vue';
    import { type SurveyInList, type statusName, type statusObj, type status } from './SurveysTypes';
    import { loadSurveys, endSurvey, loadSurveyCount, deleteSelected } from './Surveys.access';
    import { bsAlert } from '@shared/components/bsAlerts';
    import { ScopeAccess, vRequireScopeParams } from '@shared/directives/Scope.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { setupTags } from '@shared/components/Tags/Tags';
    import { AccountAccess } from '@cwd/Account/Account.access';
    import { combineUrlSegments } from '@shared/utils/combineUrlSegments';
    import { Permissions } from '@shared/Root/Permissions';
    import { useRoute } from 'vue-router';
    import Preview from '@shared/components/Preview.vue';

    type EventToggle = 'all' | 'eventRelated' | 'notEventRelated';

    const route = useRoute();

    const props = withDefaults(
        defineProps<{
            hideTemplates?: boolean;
            onlyTemplates?: boolean;
            selectedId?: string | null | undefined;
        }>(),
        {}
    );

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const accountAccess: AccountAccess = injectorAccess.injector.get(AccountAccess);
    const tags = setupTags(accountAccess.state.currentAccount?.AccountId);

    const surveys: status = {
        drafts: { description: $Resources.SurveysDraftsIntroduction, display: $Resources.SurveysDraftsTab, status: 10, name: 'drafts' },
        scheduled: {
            description: $Resources.SurveysScheduledIntroduction,
            display: $Resources.SurveysScheduledTab,
            status: 0,
            name: 'scheduled',
            hidden: true,
        },
        active: { description: $Resources.SurveysActiveIntroduction, display: $Resources.SurveysActiveTab, status: 20, name: 'active' },
        ended: { description: $Resources.SurveysEndedIntroduction, display: $Resources.SurveysEndedTab, status: 30, name: 'ended', hidden: true },
        templates: { description: $Resources.SurveysTemplateIntroduction, display: $Resources.TemplatesTab, status: 110, name: 'templates', hidden: true },
    };

    const state = reactive<{
        items: SurveyInList[];
        isBusy: boolean;
        currentTab: null | statusName;
        tabData: null | statusObj;
        tabs: tabs;
        selected: string[];
        hasSurveyScope: boolean;
        hasTemplatesScope: boolean;
        previewUrl: string | null;
        eventToggle: EventToggle;
        isCreateNew: boolean;
    }>({
        items: [],
        isBusy: false,
        currentTab: null,
        tabData: null,
        tabs: surveys,
        selected: [],
        hasSurveyScope: false,
        hasTemplatesScope: false,
        previewUrl: null,
        eventToggle: 'all',
        isCreateNew: false,
    });

    if (route.path.toLocaleLowerCase() === '/surveys/new') {
        state.isCreateNew = true;
    }

    onMounted(async () => {
        const requireTemplatesScopeParams: vRequireScopeParams = {
            Feature: 'SurveyTemplates',
            Permission: Permissions.Read,
        };

        state.hasTemplatesScope = scopeAccess.hasScope(requireTemplatesScopeParams);

        const requireScopeParams: vRequireScopeParams = {
            Feature: 'Surveys',
            Permission: Permissions.Write,
        };

        state.hasSurveyScope = scopeAccess.hasScope(requireScopeParams);

        if (props.onlyTemplates) {
            for (const [key, value] of Object.entries(state.tabs)) {
                state.tabs[key].hidden = true;
            }

            switchTab('templates');
        }

        if (state.hasTemplatesScope && !props.hideTemplates) {
            state.tabs.templates.hidden = false;
        }

        if (props.onlyTemplates) return;

        const location = window.location;
        const url = new URL(location.toString());
        const params = url.searchParams;

        loadSurveyCount().then((data) => {
            data.ScheduledSurveyCount && (state.tabs.scheduled.hidden = false);
            data.EndedSurveyCount && (state.tabs.ended.hidden = false);
        });

        switchTab((params.get('tab') as statusName) || surveys.drafts.name);

        if (params.get('published')) {
            bsAlert({ title: $Resources.PublishedSurveyHeading, content: $Resources.PublishedSurveyBody });
        }
    });

    const switchTab = async (statusName: statusName) => {
        if (state.isBusy) return;

        clearSelection();

        state.currentTab = statusName;
        state.isBusy = true;
        state.items = [];

        state.tabData = {
            description: surveys[statusName].description,
            status: surveys[statusName].status,
            name: surveys[statusName].name,
            display: 'display',
        };

        onLoadSurveys();
    };

    const onLoadSurveys = async () => {
        let query = getQuery();
        if (!query) return;

        await loadSurveys(query)
            .then((data: SurveyInList[]) => {
                state.items = data;
            })
            .finally(() => (state.isBusy = false));
    };

    const getQuery = () => {
        if (!state.currentTab) return;
        return getStatusFilter(state.currentTab);
    };

    const currentDateTime = () => {
        let d: Date | string = new Date(Date.now());
        d = d.toISOString();
        d = d.toString();
        return d.substring(0, d.length - 5);
    };

    const getStatusFilter = (statusName: statusName) => {
        let filter = '?$filter=(Status eq ';
        let currentStatus = surveys[statusName].status;

        if (currentStatus === surveys.scheduled.status) {
            filter += surveys.active.status + " and Start eq null or Start gt DateTime'" + currentDateTime() + "')";
        } else if (currentStatus === surveys.active.status) {
            filter +=
                currentStatus +
                " and Start eq null or Start lt DateTime'" +
                currentDateTime() +
                "'" +
                " and (End eq null or End gt DateTime'" +
                currentDateTime() +
                "'))";
        } else if (currentStatus === surveys.ended.status) {
            filter += currentStatus + " or End lt DateTime'" + currentDateTime() + "')";
        } else {
            filter += currentStatus + ')';
        }

        return filter;
    };

    const filterOptions = () => {
        return ['Title', 'SurveyName', 'Description', 'Event.Name'];
    };

    const orderOptions = () => {
        return [
            { display: $Resources.LastModifiedLabel, key: 'LastModified' },
            { display: $Resources.SurveysSortByTitleLabel, key: 'Title' },
            { display: $Resources.SurveysSortByNameLabel, key: 'SurveyName' },
        ];
    };

    const onDeleteSelected = () => {
        if (state.isBusy) return;
        state.isBusy = true;

        let list = state.selected.map((id: string) => {
            return {
                SurveyId: id,
            };
        });

        deleteSelected(list)
            .then(() => {
                state.selected.forEach((id: string) => {
                    let found = state.items.find((item) => {
                        return id === item.SurveyId;
                    });

                    found && (found.Deleted = true);
                });

                bsAlert({ title: $Resources.AlertSurveysDeletedHeading, content: $Resources.AlertSurveysDeletedBody });
                clearSelection();
            })
            .catch(() => {
                bsAlert({ type: 'danger', title: $Resources.ErrorDeletingSurveysHeading, content: $Resources.ErrorDeletingSurveysBody });
            })
            .finally(() => {
                state.isBusy = false;
            });
    };

    const clearSelection = () => {
        state.selected = [];
    };

    const filteredItems = computed(() => {
        return state.items.filter((item) => {
            return !item.Deleted && toggleEvents(item);
        });
    });

    const toggleEventsOptions = () => {
        return [
            { display: $Resources.ToggleOptionsAll, key: 'all' },
            { display: $Resources.ToggleOptionsEventRelated, key: 'eventRelated' },
            { display: $Resources.ToggleOptionsNotEventRelated, key: 'notEventRelated' },
        ];
    };

    const toggleEvents = (item: SurveyInList) => {
        switch (state.eventToggle) {
            case 'eventRelated':
                return item.Event;
            case 'notEventRelated':
                return !item.Event;
            default:
                return true;
        }
    };

    const showPreview = (surveyId: string) => {
        state.previewUrl = combineUrlSegments(contextAccess.state.currentAccount!.PublicEventUrl.PublicHost, 'Surveys', '' + surveyId, '?preview=true');
    };

    const onEndSurvey = (id: string) => {
        if (state.isBusy) return;
        state.isBusy = true;

        endSurvey(id)
            .then(() => {
                state.isBusy = false;
                bsAlert({ title: $Resources.AlertSurveyEndedHeader, content: $Resources.AlertSurveyEndedBody });
                switchTab(surveys.ended.name);
            })
            .catch(() => {
                state.isBusy = false;
                bsAlert({ type: 'danger', title: $Resources.ErrorSurveyNotEndedHeader, content: $Resources.ErrorSurveyNotEndedBody });
            });
    };
</script>

<script lang="ts"></script>

<template>
    <List
        v-if="state.currentTab && state.tabData"
        itemId="SurveyId"
        :title="state.isCreateNew ? undefined : $Resources.SurveysHeading"
        :description="state.tabData.description"
        :items="filteredItems"
        :tabs="state.tabs"
        :currentTab="state.currentTab"
        :loading="state.isBusy"
        :filterKeys="filterOptions()"
        :orderOptions="orderOptions()"
        :checkedItems="state.isCreateNew ? null : state.selected"
        :tags="tags"
        :disableCheckboxes="state.currentTab === 'scheduled' || state.currentTab === 'active'"
        :selectedToggle="state.eventToggle"
        :toggleOptions="toggleEventsOptions()"
        :recycleManager="state.isCreateNew ? false : true"
        :selectedId="props.selectedId"
        @update:tab="(newVal) => switchTab(newVal as statusName)"
        @update:checkedItems="(newVal) => (state.selected = newVal as string[])"
        @clearSelected="clearSelection"
        @deleteSelected="onDeleteSelected"
        @update:selectedToggle="(newVal) => (state.eventToggle = newVal as EventToggle)">
        <template #toolbar>
            <router-link
                v-show="state.selected.length === 0"
                v-if="!state.isCreateNew && state.hasSurveyScope"
                :to="{ name: 'SurveysNew' }"
                class="btn btn-default">
                <span class="glyphicon glyphicon-plus m-xxs-r" v-show="state.selected.length === 0"></span>
                {{ $Resources.NewSurveyButton }}
            </router-link>
        </template>
        <template #item="{ item, index }">
            <ListItem
                :survey="item"
                :currentTab="state.currentTab"
                :tags="tags"
                :isCreateNew="state.isCreateNew"
                @showPreview="(id) => showPreview(id)"
                @endSurvey="(id) => onEndSurvey(id)"
                @clickedSurveyId="(newVal) => $emit('clickedSurveyId', newVal)"></ListItem>
        </template>
    </List>

    <Preview :previewUrl="state.previewUrl" @close="state.previewUrl = null" />
</template>

<style scoped lang="less"></style>

<style lang="less"></style>
